import { CONSTANTS } from '@pk/powerkioskutils';
import * as XLSX from '@sheet/core';

import * as _ from 'lodash';

import { Attachment } from '../attachment';
import { CommissionType } from '../commission-type';
import { PendingCommission } from '../pending-commission';
import { SupplierCommissionAttachmentSheet } from '../supplier-commission-attachment-sheet';
import { SupplierCommissionMap } from '../supplier-commission-map';
import { ViewCommissionReport } from '../view-commission-report';
import { AbstractPreviewReport } from './preview-report-abstract';

export class UploadPreviewReport extends AbstractPreviewReport {
	public canEditSheetMapping = true;
	public suffixLabel = '';

	public sheet: SupplierCommissionAttachmentSheet;
	public supplierCommissionMaps: SupplierCommissionMap[];
	public commissionTypes: CommissionType[];
	public attachment: Attachment;
	public report: ViewCommissionReport;

	constructor(
		sheet: SupplierCommissionAttachmentSheet,
		supplierCommissionMaps: SupplierCommissionMap[],
		commissionTypes: CommissionType[],
		attachment: Attachment,
		report: ViewCommissionReport,
	) {
		super();
		this.sheet = sheet;
		this.supplierCommissionMaps = supplierCommissionMaps;
		this.commissionTypes = commissionTypes;
		this.attachment = attachment;
		this.report = report;
	}

	// Matches SupplierCommissionMap.entity.ts
	private updateSheetRange(worksheet?: XLSX.WorkSheet): void {
		if (worksheet['!autofilter']) {
			const range = { s: { r: Infinity, c: Infinity }, e: { r: 0, c: 0 } };
			Object.keys(worksheet)
				.filter(x => x.charAt(0) !== '!')
				.map(XLSX.utils.decode_cell)
				.forEach(x => {
					range.s.c = Math.min(range.s.c, x.c);
					range.s.r = Math.min(range.s.r, x.r);
					range.e.c = Math.max(range.e.c, x.c);
					range.e.r = Math.max(range.e.r, x.r);
				});
			worksheet['!ref'] = XLSX.utils.encode_range(range);
		}
	}

	public async setup() {
		if (!this.commissionTypes) {
			this.commissionTypes = (await this.graphqlService.getCommissionTypeListData())?.data?.commissionTypes?.message;
		}
		this.rawData = await this.getRawData();
		this.pendingCommissions = await this.getMappedData();
		this.totalSupplierPayment = _.sumBy(this.sheet.pendingCommissions, pc => parseFloat(String(pc.supplierPayment)));
	}

	public async getRawData(): Promise<any[]> {
		const binary = window.atob(this.attachment.attachmentBase64.response);
		const array = [];
		for (let j = 0; j < binary.length; j++) {
			array.push(binary.charCodeAt(j));
		}
		const file = new Blob([new Uint8Array(array)], {
			type: `${this.attachment.contentType}/${this.attachment.contentSubType}`,
		});

		const rawData: any[] = await new Promise(async (resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (ev: any) => {
				try {
					const fileResult = new Uint8Array(ev.target.result);
					const workbook = XLSX.read(fileResult, { type: 'array' });
					const workSheet = workbook.Sheets[this.sheet.name];
					this.updateSheetRange(workSheet);
					// eslint-disable-next-line @typescript-eslint/naming-convention
					const data = XLSX.utils.sheet_to_csv(workSheet, { FS: '|' });
					resolve(
						this.sheet?.supplierCommissionMap?.headerRow ? data.split('\n').slice(this.sheet.supplierCommissionMap.headerRow - 1) : data.split('\n'),
					);
				} catch (e) {
					this.toastrService.warning(
						'There was a problem reading the file. We have been notified and are working to fix the issue. ' +
							'Please check back again in 30 minutes.',
						'Commissions',
					);
					reject(e);
				}
			};
			reader.readAsArrayBuffer(file);
		});

		return rawData;
	}
	public async getMappedData(): Promise<PendingCommission[]> {
		return _.take(this.sheet.pendingCommissions, 5);
	}

	public async updateSheetMapping(supplierCommissionMapId: number): Promise<void> {
		try {
			this.loading = true;
			const result = await this.graphqlService.updateSupplierCommissionAttachmentSheet(this.sheet.id, { supplierCommissionMapId });
			this.sheet = new SupplierCommissionAttachmentSheet(result.data.updateSupplierCommissionAttachmentSheet);

			await this.setup();
		} catch (e) {
			this.userAuditService.captureEvent(CONSTANTS.userAuditTypes.code, e.message, 'upload-preview-report:updateSheetMapping');
			this.toastrService.warning('Could not update sheet mapping', 'Commissions');
		} finally {
			this.loading = false;
		}
	}

	public async applySystemGeneratedMapping() {
		try {
			this.loading = true;
			const result = await this.graphqlService.applySystemGeneratedMapping(this.sheet.id);
			if (result.data.applySystemGeneratedMapping.didGenerateSystemMapping) {
				this.sheet = new SupplierCommissionAttachmentSheet(result.data.applySystemGeneratedMapping.sheet);
				await this.setup();

				return true;
			} else {
				this.toastrService.warning(
					'We were unable to map the data below to any existing maps, or determine a potential new mapping. Please try another mapping from the dropdown, or manually add a new map.',
					'Commissions',
				);
			}
		} catch (e) {
			this.userAuditService.captureEvent(CONSTANTS.userAuditTypes.code, e.message, 'upload-preview-report:applySystemGeneratedMapping');
			this.toastrService.warning('Could not update sheet mapping', 'Commissions');
		} finally {
			this.loading = false;
		}
		return false;
	}
}
